import React, { useEffect, useState } from 'react'
import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import Login from '../pages/login/Login'
import { CSpinner } from '@coreui/react'
import { validateMsalSession, isBrowserRestoredSession } from '../utils/sessionUtils'

const AuthGuard = ({ children }) => {
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [isValidatingSession, setIsValidatingSession] = useState(true)
  const [sessionValidated, setSessionValidated] = useState(false)

  useEffect(() => {
    const validateSession = async () => {
      try {
        // Check if this might be a browser restore scenario
        const isBrowserRestore = isBrowserRestoredSession()

        if (isBrowserRestore) {
          console.log('Detected browser restore scenario, validating session...')
        }

        // Validate the current MSAL session
        const isValidSession = await validateMsalSession()

        if (!isValidSession && isAuthenticated) {
          // Session is stale but MSAL thinks we're authenticated
          console.log('Stale session detected, forcing re-authentication')
          // Force a page refresh to restart the auth flow
          window.location.reload()
          return
        }

        setSessionValidated(true)
      } catch (error) {
        console.error('Session validation error:', error)
        setSessionValidated(true) // Continue anyway
      } finally {
        setIsValidatingSession(false)
      }
    }

    // Only validate if we think we're authenticated or if interactions are done
    if ((isAuthenticated || inProgress === InteractionStatus.None) && !sessionValidated) {
      validateSession()
    }
  }, [instance, isAuthenticated, inProgress, sessionValidated])

  // Show loading spinner during authentication process or session validation
  if (
    inProgress === InteractionStatus.Login ||
    inProgress === InteractionStatus.SsoSilent ||
    isValidatingSession
  ) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <CSpinner color="primary" size="lg" />
        <div className="ms-3">
          {isValidatingSession ? 'Validating session...' : 'Authenticating...'}
        </div>
      </div>
    )
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login />
  }

  // Render protected content if authenticated and session is valid
  return children
}

export default AuthGuard
